import { integrationsClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';
import type { ApiResponse } from '~/features/onboarding/types/connection.types';

import type {
  ProductListFilters,
  ProductListParams,
  ProductListRequest,
  ProductListResponse,
} from '../types/product-list.types';

/**
 * Fetch a paginated, filterable product list for a connection. Keeps the
 * outward API surface flat (`ProductListParams`) while shaping the body into
 * the backend's `{ filters, pagination, sortBy, syncId, ingestionDate }`
 * envelope. Sort is sent both as the top-level `field_dir` token and as the
 * structured `pagination.sortBy` + `pagination.sortOrder`. Empty filter object
 * is omitted to keep the request minimal.
 */
export async function fetchProductList(params: ProductListParams): Promise<ProductListResponse> {
  // Merge free-text query + structured filter-drawer selections into one body.filters.
  const filters: ProductListFilters = {
    ...(params.appliedFilters as ProductListFilters | undefined),
  };
  if (params.query) filters.query = params.query;

  const sortOrder = params.sortOrder ?? 'ASC';

  const body: ProductListRequest = {
    pagination: {
      page: params.page,
      size: params.size,
      // Structured sort inside the pagination meta.
      ...(params.sortBy ? { sortBy: params.sortBy, sortOrder } : {}),
    },
    ...(Object.keys(filters).length > 0 ? { filters } : {}),
    // Combined `field_dir` token alongside the structured pagination sort.
    ...(params.sortBy ? { sortBy: `${params.sortBy}_${sortOrder.toLowerCase()}` } : {}),
    syncId: params.syncId ?? null,
    ...(params.ingestionDate ? { ingestionDate: params.ingestionDate } : {}),
  };

  // A candidate-scope file goes to the by-ids-file route: the id list never
  // travels inline, so the endpoint's 4,000-value budget doesn't apply. The
  // response shape is identical, so callers need no branch.
  if (params.idsFile) {
    const form = new FormData();
    form.append('file', params.idsFile, params.idsFile.name);
    if (params.idsFilterKey) form.append('idsFilterKey', params.idsFilterKey);
    // The rest of the endpoint-1 body rides as a JSON *string* part.
    form.append('filters', JSON.stringify(body));
    const fileRes = await integrationsClient
      .post(endpoints.products.listByIdsFile(params.connectionId), { body: form })
      .json<ApiResponse<ProductListResponse>>();
    return fileRes.data;
  }

  const res = await integrationsClient
    .post(endpoints.products.list(params.connectionId), { json: body })
    .json<ApiResponse<ProductListResponse>>();
  return res.data;
}
